import { isEmpty } from 'lodash';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { WhatsApp } from 'constants/messageMediums';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { MessageMedium, MetaApprovalStatusDefinition } from 'modules/graphql/types';
import { LocaleSwitcher } from 'modules/templateEditor/common/LocaleSwitcher';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { View24 } from 'ui/icons';
import { LinkButton } from 'ui/linkButton';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { useHasActiveWhatsAppLocaleMatchingStatus } from 'modules/templateEditor/hooks/useHasActiveWhatsAppLocaleMatchingStatus';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useTemplateQueryContext } from '../../context/TemplateQueryContext';
import { displayContent } from '../content';
import { EditButtonCombo, OverflowButton, SendTestMessage } from '../Controls';
import { WhatsAppTemplateReviewButton } from '../Controls/WhatsAppTemplateReviewButton';
import { ContinueToReviewButton } from './ContinueToReviewButton';

const LocaleSwitchWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${({ theme }) => theme.spacing.values.plusHalf};
  @media screen and (max-width: ${pxToRem(893)}) {
    margin-bottom: 0;
    margin-right: ${spacing.half};
  }
`;
const TemplateEditorActionButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: ${spacing.half};
  @media screen and (max-width: ${pxToRem(893)}) {
    justify-content: flex-start;
  }
`;

const PageActionWrapper = styled.div`
  @media screen and (max-width: ${pxToRem(893)}) {
    display: flex;
  }
`;

const LinkButtonText = styled.div`
  padding-left: ${spacing.half};
`;

export const PageActions = () => {
  const { medium } = useTemplateQueryContext();
  const { locales } = useLocaleSettingsContext();
  const { campaignId, experimentFlow: isExperimentFlow } = useQueryParamContext();
  const isCampaignFlow = !!campaignId;
  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  searchParams.set('view', 'preview');
  const templateEditPreview = `${pathname}?${searchParams.toString()}`;

  const { activeWhatsAppTemplateLocales } = useLocalesQueryContext();
  const hasRejectedWhatsAppLocale = useHasActiveWhatsAppLocaleMatchingStatus(
    activeWhatsAppTemplateLocales,
    MetaApprovalStatusDefinition.Rejected,
  );
  const canPreview = !hasRejectedWhatsAppLocale;

  return (
    <PageActionWrapper>
      {!isEmpty(locales) && (
        <LocaleSwitchWrapper>
          <LocaleSwitcher isReadOnly={hasRejectedWhatsAppLocale} />
        </LocaleSwitchWrapper>
      )}
      <TemplateEditorActionButtonsWrapper>
        <OverflowButton />
        <SendTestMessage />
        {medium === MessageMedium.Email ? (
          <EditButtonCombo />
        ) : (
          canPreview && (
            <LinkButton
              data-test={'template-editor-preview-button'}
              to={templateEditPreview}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              <View24 />
              <LinkButtonText>{displayContent.btnPreview}</LinkButtonText>
            </LinkButton>
          )
        )}
        {medium === WhatsApp && <WhatsAppTemplateReviewButton />}
        {isCampaignFlow && !isExperimentFlow && <ContinueToReviewButton />}
      </TemplateEditorActionButtonsWrapper>
    </PageActionWrapper>
  );
};

export default PageActions;
